import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { useAuthUsers } from '@/hooks/useAuthUsers';
import { useAuthorization } from '@/hooks/useAuthorization';
import { USER_ROLES } from '@/types/enums';
import { MultiSelect } from '@/components/ui/multi-select';

type Candidate = Omit<Tables<'candidates'>, 'interviewer'> & { joining_date?: string | null; interviewer?: string[] | string | null };

interface FormData {
  expected_ctc: string | null;
  note: string;
  joining_date: string;
  interviewer: string[];
}

 interface CandidateCtcNoteFormProps {
   candidate: Candidate;
   onSaved?: () => void;
 }

export function CandidateCtcNoteForm({ candidate, onSaved }: CandidateCtcNoteFormProps) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const { data: authUsers } = useAuthUsers();
  const { role } = useAuthorization();
  const normalizeInterviewerValue = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      return [value];
    }

    return [];
  };

  const { register, handleSubmit, reset, control } = useForm<FormData>({
    defaultValues: {
      expected_ctc: candidate.expected_ctc ?? null,
      note: candidate.note ?? '',
      joining_date: candidate.joining_date ?? '',
      interviewer: normalizeInterviewerValue(candidate.interviewer),
    },
  });

   const onSubmit = async (data: FormData) => {
     setIsSaving(true);
     try {
      const updateData: { expected_ctc: string | null; note: string; interviewer: string[] | null; joining_date?: string | null } = {
        expected_ctc: data.expected_ctc,
        note: data.note,
        interviewer: data.interviewer.length > 0 ? data.interviewer : null,
      };

      if (role === USER_ROLES.HR) {
        updateData.joining_date = data.joining_date || null;
      }

      const { error } = await supabase
        .from('candidates')
        .update(updateData as never)
        .eq('id', candidate.id);
       if (error) throw error;
       toast({ title: 'Saved', description: 'Candidate details updated' });
       onSaved?.();
       reset(data);
     } catch (err) {
       console.error('Save error:', err);
       toast({ title: 'Error', description: 'Failed to save', variant: 'destructive' });
     } finally {
       setIsSaving(false);
     }
   };

   return (
     <Card>
       <CardHeader>
         <CardTitle>Notes & Expected CTC</CardTitle>
       </CardHeader>
       <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="expected_ctc">Expected CTC</Label>
          <Input id="expected_ctc" type="text" {...register('expected_ctc')} />
        </div>
        <div>
          <Label htmlFor="interviewer">Interviewers</Label>
          <Controller
            name="interviewer"
            control={control}
            render={({ field }) => (
              <MultiSelect
                options={authUsers?.users.map((u) => ({ label: u.email || u.id, value: u.id })) ?? []}
                value={normalizeInterviewerValue(field.value)}
                onChange={field.onChange}
                placeholder="Select interviewers"
              />
            )}
          />
        </div>
        {role === USER_ROLES.HR && (
          <div>
            <Label htmlFor="joining_date">Joining Date</Label>
            <Input id="joining_date" type="date" {...register('joining_date')} />
          </div>
        )}
          <div>
            <Label htmlFor="note">Notes</Label>
            <Textarea id="note" rows={4} {...register('note')} />
          </div>
           <Button type="submit" disabled={isSaving} className="w-full">
             {isSaving ? 'Saving...' : 'Save'}
           </Button>
         </form>
       </CardContent>
     </Card>
   );
 }
